import { useEffect } from 'react'
import { Check, AlertCircle, Loader2 } from 'lucide-react'
import { Portal } from '../portal'
import { cn } from '@/lib/utils'

export type ToastType = 'success' | 'loading' | 'error'

interface ToastProps {
  isOpen: boolean
  onClose: () => void
  type?: ToastType
  message: string
  /** Progress value 0-100 for loading toasts */
  progress?: number
  /** Whether the loading is complete (shows green bar + checkmark) */
  isComplete?: boolean
  /** Auto-close after ms (default 2000, set to 0 to disable) */
  duration?: number
}

export function Toast({ 
  isOpen, 
  onClose, 
  type = 'success', 
  message, 
  progress,
  isComplete = false,
  duration = 2000 
}: ToastProps) {
  // Auto-close for non-loading toasts or completed loading toasts
  useEffect(() => {
    if (isOpen && duration > 0 && (type !== 'loading' || isComplete)) {
      const timer = setTimeout(onClose, duration)
      return () => clearTimeout(timer)
    }
  }, [isOpen, duration, type, isComplete, onClose])

  if (!isOpen) return null

  // Determine which icon to show
  const getIcon = () => {
    if (isComplete) {
      return <Check className="size-4 text-green-400" />
    }
    switch (type) {
      case 'success':
        return <Check className="size-4 text-green-400" />
      case 'loading':
        return <Loader2 className="size-4 text-white/70 animate-spin" />
      case 'error':
        return <AlertCircle className="size-4 text-red-400" />
    }
  }

  return (
    <Portal>
      <div className="fixed inset-x-0 top-0 z-40 flex justify-center pointer-events-none animate-slide-down">
        <div className={cn(
          "mt-20 mx-4 px-6 py-4 rounded-2xl bg-black/90 backdrop-blur-xl border border-white/20 shadow-2xl",
          progress !== undefined ? "min-w-[280px]" : "flex items-center gap-3"
        )}>
          {progress !== undefined ? (
            <>
              <div className="flex items-center gap-3 mb-3">
                {getIcon()}
                <span className="text-sm text-white font-medium">{message}</span>
              </div>
              <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                <div 
                  className={cn(
                    "h-full rounded-full transition-all duration-300 ease-out",
                    isComplete ? "bg-green-500" : "bg-white"
                  )}
                  style={{ width: `${Math.min(progress, 100)}%` }}
                />
              </div>
            </>
          ) : (
            <>
              {getIcon()}
              <span className="text-sm text-white font-medium">{message}</span>
            </>
          )}
        </div>
      </div>
    </Portal>
  )
}
